import { FC } from "react";
import "./style.scss";
import { useTranslation } from "react-i18next";
import CountryChevron from "../../../../../assets/CountryChevron.svg";
import { GetCountryByCode } from "static/countries";
import Close from "../../../../../assets/Close.svg";
import CameraIcon from "../../../../../assets/CameraIcon.svg";
import CheckmarkGray from "../../../../../assets/CheckmarkGray.svg";
import AccOptionIcon from "../../../../../assets/AccOptionIcon.svg";
import { useNavigate } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";

type CredentialsBoxDropdownProps = {
  flag: string;
  label: string;
  selected: string;
  onClick: () => void;
  disable: boolean;
};

const CredentialsBoxDropdown: FC<CredentialsBoxDropdownProps> = ({
  flag,
  label,
  selected,
  onClick,
  disable,
}) => (
  <div
    className="credentials-box-dropdown"
    onClick={() => !disable && onClick()}
  >
    <label>{label}</label>
    <img className="credentials-box-dropdown-flag" src={flag} alt="flag" />
    <div>
      <p>{selected}</p>
    </div>
    {!disable && (
      <img
        className="credentials-box-dropdown-chevron"
        src={CountryChevron}
        alt="chevron"
      />
    )}
  </div>
);

type CredentialsBoxPhotosProps = {
  label: string;
  marked: boolean;
  onClick: () => void;
};

export const CredentialsBoxPhotos: FC<CredentialsBoxPhotosProps> = ({
  label,
  marked,
  onClick,
}) => {
  return (
    <>
      {!marked ? (
        <div onClick={onClick} className="credentials-box-photos-marked">
          <p>{label}</p>
          <div>
            <img
              className="credentials-box-photos-marked-camera"
              src={CameraIcon}
              alt="CameraIcon"
            />
            <img
              className="credentials-box-photos-marked-acc-option"
              src={AccOptionIcon}
              alt="AccOptionIcon"
            />
          </div>
        </div>
      ) : (
        <div onClick={onClick} className="credentials-box-photos">
          <img src={CheckmarkGray} alt="CheckmarkGray" />
          <p>{label}</p>
          <img
            className="credentials-box-photos-marked-acc-option"
            src={AccOptionIcon}
            alt="AccOptionIcon"
          />
        </div>
      )}
    </>
  );
};

type CredentialsBoxProps = {
  disable: boolean;
  item: {
    main?: boolean;
    country: string;
    fields: {
      passportOrIdCard?: boolean;
      internationalPassport?: boolean;
      selfie?: boolean;
      proofOfAddress?: boolean;
      personalAddress?: boolean;
    };
    idCardFront?: string;
    idCardBack?: string;
    passport?: string;
    selfie?: string;
    proofOfAddress?: string;
    internationalPassport?: string;
    removable?: boolean;
  };
  openModal: () => void;
  // setIdCardFront: (value: string) => void;
  // setIdCardBack: (value: string) => void;
  // setPassport: (value: string) => void;
  // setSelfie: (value: string) => void;
  // setProofOfAddress: (value: string) => void;
  // setInternationalPassport: (value: string) => void;
  remove: () => void | undefined;
  country: string;
  showValidationErrors: boolean;
  isReadOnly?: boolean;
  kyc?: any;
  only_armenia_resident?: boolean | null;
  setOnlyArmeniaResident?: (value: boolean | null) => void;
};

export const CredentialsBox: FC<CredentialsBoxProps> = ({
  item,
  openModal,
  remove,
  disable,
  kyc,
  only_armenia_resident,
  setOnlyArmeniaResident,
}) => {
  const { t: o } = useTranslation("onboarding");
  const navigate = useNavigate();

  const hasDocument = (docType: string) => {
    if (!kyc?.documents) return false;

    let docsToCheck;

    if (item.main) {
      docsToCheck = kyc.documents;
    } else {
      if (!kyc.documents.OTHER_DOCUMENTS) return false;

      const countryDocs = kyc.documents.OTHER_DOCUMENTS.find(
        (doc: any) => doc.COUNTRY === item.country
      );

      if (!countryDocs?.DOCS) return false;
      docsToCheck = countryDocs.DOCS;
    }

    switch (docType) {
      case "idCard":
        return (
          !!(docsToCheck.ID_CARD_FRONT_SIDE && docsToCheck.ID_CARD_BACK_SIDE) ||
          !!docsToCheck.PASSPORT
        );
      case "internalPassport":
        return !!docsToCheck.INTERNAL_PASSPORT;
      case "selfie":
        return !!docsToCheck.SELFIE;
      case "proofOfAddress":
        return !!docsToCheck.UTILITY_BILL;
      default:
        return false;
    }
  };

  return (
    <div className="credentials-box-container">
      {item.removable && (
        <img
          className="credentials-box-close-icon"
          src={Close}
          alt="close"
          onClick={() => remove()}
        />
      )}
      <CredentialsBoxDropdown
        selected={GetCountryByCode(item.country)?.name || ""}
        flag={GetCountryByCode(item.country)?.flag || ""}
        onClick={() => openModal()}
        label={o("citizenship")}
        disable={false}
        // label={item.main ? o("currentCitizenship") : o("citizenship")}
        // disable={disable}
      />

      {item.fields.passportOrIdCard && (
        <CredentialsBoxPhotos
          onClick={() =>
            navigate(PATH_LIST.chooseDocType, {
              state: {
                country: item.main ? "" : item.country,
                citizenship: item.main ? item.country : "",
              },
            })
          }
          label={item.country === "ARM" ? o("passportOrIdCard") : o("passport")}
          marked={hasDocument("idCard")}
        />
      )}
      {item.fields.internationalPassport && (
        <CredentialsBoxPhotos
          onClick={() =>
            navigate(PATH_LIST.chooseDocType, {
              state: {
                country: item.main ? "" : item.country,
                citizenship: item.main ? item.country : "",
                type: "internalPassport",
              },
            })
          }
          label={o("internationalPassport")}
          marked={hasDocument("internalPassport")}
        />
      )}
      {item.fields.selfie && (
        <CredentialsBoxPhotos
          onClick={() =>
            navigate(PATH_LIST.selfieWithDocument, {
              state: {
                country: "",
                citizenship: item.main ? item.country : "",
              },
            })
          }
          label={o("selfieWithDocument")}
          marked={hasDocument("selfie")}
        />
      )}
      {item.fields.personalAddress && (
        <CredentialsBoxPhotos
          onClick={() =>
            navigate(PATH_LIST.residenceAddress, {
              state: {
                country: "",
                citizenship: item.main ? item.country : "",
              },
            })
          }
          label={o("residentialAddress")}
          marked={kyc?.address_json !== null}
        />
      )}
      {item.fields.proofOfAddress && (
        <CredentialsBoxPhotos
          onClick={() =>
            navigate(PATH_LIST.proofOfAddress, {
              state: {
                country: "",
                citizenship: item.main ? item.country : "",
              },
            })
          }
          label={o("proofOfAddress")}
          marked={hasDocument("proofOfAddress")}
        />
      )}

      {item.main && item.country === "ARM" && setOnlyArmeniaResident && (
        <div className="credentials-box-radio-section">
          <div className="credentials-box-radio-group">
            <label className="credentials-box-radio-item">
              <input
                type="radio"
                name="armeniaResidency"
                value="only"
                checked={only_armenia_resident === true}
                onChange={() => setOnlyArmeniaResident(true)}
              />
              <span className="credentials-box-radio-label">
                {o("onlyArmeniaResident")}
              </span>
            </label>
            <label className="credentials-box-radio-item">
              <input
                type="radio"
                name="armeniaResidency"
                value="another"
                checked={only_armenia_resident === false}
                onChange={() => setOnlyArmeniaResident(false)}
              />
              <span className="credentials-box-radio-label">
                {o("haveAnotherResidency")}
              </span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};
